import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES, seqColor } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { int, money, niceDate, WEEKDAYS } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty } from '../components/ui';

// Staffing vs demand by hour: order volume against labor coverage from shift
// punches, so managers can spot slots that are over- or under-staffed.
// Dollar labor cost is intentionally omitted (wage data is unreliable).
const fmtHour = (h) => {
  const ampm = h < 12 ? 'a' : 'p';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${ampm}`;
};

export default function StaffingPage() {
  const { reportParams, startDate, endDate } = useFilters();
  const [scope, setScope] = useState('all'); // 'all' or weekday index 0-6
  const { data, loading, error } = useReport('staffing-demand', reportParams);

  const view = useMemo(() => {
    if (!data) return null;
    const { cells, weekdayCounts } = data;
    const inScope = scope === 'all' ? cells : cells.filter(c => c.weekday === Number(scope));
    // occurrences of the selected weekday(s), for averaging
    const occ = scope === 'all'
      ? weekdayCounts.reduce((a, b) => a + b, 0)
      : weekdayCounts[Number(scope)];

    const byHour = Array.from({ length: 24 }, (_, hr) => ({ hr, orders: 0, netSales: 0, laborHours: 0 }));
    for (const c of inScope) {
      byHour[c.hour].orders += c.orders;
      byHour[c.hour].netSales += c.netSales;
      byHour[c.hour].laborHours += c.laborHours;
    }
    // restrict the display to the operating window (first→last hour with any activity)
    const active = byHour.filter(h => h.orders > 0 || h.laborHours > 0);
    const minH = active.length ? Math.min(...active.map(h => h.hr)) : 8;
    const maxH = active.length ? Math.max(...active.map(h => h.hr)) : 23;
    const hours = byHour.slice(minH, maxH + 1);

    const peakOrders = Math.max(1, ...hours.map(h => h.orders));
    const peakLabor = Math.max(1, ...hours.map(h => h.laborHours));

    const totalOrders = inScope.reduce((a, c) => a + c.orders, 0);
    const totalLabor = inScope.reduce((a, c) => a + c.laborHours, 0);

    // Most out-of-step hours: demand share minus staffing share of their peaks
    let mostUnder = null, mostOver = null;
    for (const h of hours) {
      if (h.orders < 5) continue;
      const gap = (h.orders / peakOrders) - (h.laborHours / peakLabor);
      if (!mostUnder || gap > mostUnder.gap) mostUnder = { ...h, gap };
      if (!mostOver || gap < mostOver.gap) mostOver = { ...h, gap };
    }

    return { hours, occ, peakOrders, peakLabor, totalOrders, totalLabor, mostUnder, mostOver };
  }, [data, scope]);

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Lining up demand and shifts…" />;
  if (!view || view.hours.length === 0) return <Empty>No demand or shift data for this period.</Empty>;

  const { hours, occ, peakOrders, peakLabor, totalOrders, totalLabor, mostUnder, mostOver } = view;

  // Hero overlay: each series as % of its own peak (single % axis, honest)
  const overlay = {
    labels: hours.map(h => fmtHour(h.hr)),
    datasets: [
      {
        label: 'Demand (orders)',
        data: hours.map(h => Math.round((h.orders / peakOrders) * 100)),
        borderColor: SERIES[0],
        backgroundColor: 'rgba(42,120,214,0.08)',
        fill: true,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
      },
      {
        label: 'Staffing (labor hours)',
        data: hours.map(h => Math.round((h.laborHours / peakLabor) * 100)),
        borderColor: SERIES[2],
        borderWidth: 2,
        borderDash: [5, 4],
        pointRadius: 0,
        tension: 0.3,
      },
    ],
  };

  // Coverage heatmap: orders per labor hour by weekday × hour
  const hourSpan = hours.map(h => h.hr);
  const heatMax = Math.max(
    1,
    ...data.cells.filter(c => c.laborHours > 0).map(c => c.orders / c.laborHours)
  );
  const cellByKey = new Map(data.cells.map(c => [`${c.weekday}-${c.hour}`, c]));

  const scopeChips = [['all', 'All week'], ...WEEKDAYS.map((d, i) => [String(i), d])];

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="grid kpi-grid">
        <KpiCard label="Orders / Labor Hour" value={totalLabor ? (totalOrders / totalLabor).toFixed(1) : '—'} sub={`${int(totalOrders)} orders · ${int(Math.round(totalLabor))} labor hrs`} />
        <KpiCard label="Labor Hours" value={int(Math.round(totalLabor))} sub={scope === 'all' ? 'across the week' : `${WEEKDAYS[Number(scope)]}s (${occ} in range)`} />
        <KpiCard label="Busiest Hour" value={mostUnder ? fmtHour(hours.reduce((a, b) => b.orders > a.orders ? b : a).hr) : '—'} sub="peak order volume" />
        <KpiCard label="Likely Understaffed" value={mostUnder ? fmtHour(mostUnder.hr) : '—'} sub="highest demand vs coverage gap" />
        <KpiCard label="Likely Overstaffed" value={mostOver ? fmtHour(mostOver.hr) : '—'} sub="most coverage vs demand" invert />
      </div>

      <Card
        tour="staff-overlay"
        title="Demand vs Staffing by Hour"
        sub={`${niceDate(startDate)} – ${niceDate(endDate)} · each line scaled to its own peak, so the shapes compare directly — gaps mean the schedule and demand disagree`}
        actions={
          <div className="chip-row">
            {scopeChips.map(([id, label]) => (
              <button key={id} className={`chip ${String(scope) === id ? 'active' : ''}`} onClick={() => setScope(id === 'all' ? 'all' : id)}>{label}</button>
            ))}
          </div>
        }
      >
        <div className="chart-box">
          <Line
            data={overlay}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                  callbacks: {
                    label: ctx => {
                      const h = hours[ctx.dataIndex];
                      return ctx.datasetIndex === 0
                        ? ` Demand: ${int(h.orders)} orders (${ctx.parsed.y}% of peak)`
                        : ` Staffing: ${h.laborHours.toFixed(0)} labor hrs (${ctx.parsed.y}% of peak)`;
                    },
                  },
                },
              },
              scales: {
                y: { beginAtZero: true, max: 105, ticks: { callback: v => `${v}%` } },
                x: { grid: { display: false } },
              },
            }}
          />
        </div>
        <div className="legend-note">
          Where the blue demand line sits above the dashed staffing line, orders are outrunning coverage (understaffed); where staffing sits above demand, you're paying for idle time.
        </div>
      </Card>

      <Card tour="staff-heat" title="Coverage Heatmap — Orders per Labor Hour" sub="darker = more orders per scheduled labor hour (tighter coverage); blank = closed / no shifts">
        <div className="table-wrap">
          <table className="heatmap">
            <thead>
              <tr>
                <th />
                {hourSpan.map(h => <th key={h}>{h % 2 === 0 ? fmtHour(h) : ''}</th>)}
              </tr>
            </thead>
            <tbody>
              {WEEKDAYS.map((day, wd) => (
                <tr key={day}>
                  <td className="rowlabel">{day}</td>
                  {hourSpan.map(hr => {
                    const c = cellByKey.get(`${wd}-${hr}`);
                    const ratio = c && c.laborHours > 0 ? c.orders / c.laborHours : null;
                    return (
                      <td
                        key={hr}
                        style={{ background: ratio == null ? '#f4f3ef' : seqColor(ratio, heatMax) }}
                        title={c ? `${day} ${fmtHour(hr)} — ${int(c.orders)} orders, ${c.laborHours.toFixed(1)} labor hrs${ratio != null ? `, ${ratio.toFixed(1)} orders/hr` : ''}` : `${day} ${fmtHour(hr)} — no activity`}
                      >
                        {ratio != null && ratio >= heatMax * 0.66 ? ratio.toFixed(0) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="legend-note">Hover any cell for exact orders, labor hours, and the ratio. The busiest ratios are the slots most likely to need another hand.</div>
        </div>
      </Card>

      <Card tour="staff-hours" title="Hourly Detail" sub={scope === 'all' ? 'summed across the week' : `${WEEKDAYS[Number(scope)]}s only`}>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="no-sort">Hour</th>
                <th className="num no-sort">Orders</th>
                <th className="num no-sort">Net Sales</th>
                <th className="num no-sort">Labor Hrs</th>
                <th className="num no-sort">Orders / Labor Hr</th>
                <th className="num no-sort">Avg Orders{scope === 'all' ? '' : '/day'}</th>
              </tr>
            </thead>
            <tbody>
              {hours.map(h => (
                <tr key={h.hr}>
                  <td>{fmtHour(h.hr)}</td>
                  <td className="num">{int(h.orders)}</td>
                  <td className="num">{money(h.netSales)}</td>
                  <td className="num">{h.laborHours.toFixed(1)}</td>
                  <td className="num">{h.laborHours > 0 ? (h.orders / h.laborHours).toFixed(1) : '—'}</td>
                  <td className="num">{occ ? (h.orders / occ).toFixed(1) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
